import React from 'react';
import { MasterTransaction } from '../types';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { TrendingUp, Calendar, Users, Award, Percent, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatCurrency, formatPercentage } from '../lib/formatters';

interface Props {
  transactions: MasterTransaction[];
  allTransactions?: MasterTransaction[];
  onOpenDispute?: (context: { targetTransactionId?: string; shiftContext?: { date: string; shiftId: string } }) => void;
}

export const EmployeePayPeriodTips: React.FC<Props> = ({ transactions, allTransactions = [] }) => {
  // 1. Group personal tips by month
  const personalGrouped = transactions
    .filter(t => t.type === 'Allocated')
    .reduce((acc, tx) => {
      const dateStr = tx.tipDate || tx.timestamp || '';
      const monthYear = dateStr.substring(0, 7); // e.g., 2026-04
      if (!monthYear || monthYear.length < 7) return acc;
      if (!acc[monthYear]) acc[monthYear] = 0;
      acc[monthYear] += tx.amount || 0;
      return acc;
    }, {} as Record<string, number>);

  // 2. Group all tenant tips by month and employee email/name to figure out coworkers
  const allGroupedByMonthAndUser = allTransactions
    .filter(t => t.type === 'Allocated')
    .reduce((acc, tx) => {
      const dateStr = tx.tipDate || tx.timestamp || '';
      const monthYear = dateStr.substring(0, 7);
      if (!monthYear || monthYear.length < 7) return acc;
      
      const userKey = tx.email || tx.staffName || 'Unknown';
      
      if (!acc[monthYear]) {
        acc[monthYear] = {};
      }
      if (!acc[monthYear][userKey]) {
        acc[monthYear][userKey] = 0;
      }
      acc[monthYear][userKey] += tx.amount || 0;
      return acc;
    }, {} as Record<string, Record<string, number>>);

  // 3. Compile final list of points
  const chartData = Object.entries(personalGrouped)
    .map(([month, rawAmount]) => {
      const myAmount = rawAmount as number;
      const monthUsers = (allGroupedByMonthAndUser[month] || {}) as Record<string, number>;
      const userAmounts: number[] = Object.values(monthUsers);
      
      // Calculate coworker stats for this month
      const totalWorkers = userAmounts.length;
      const sumTips = userAmounts.reduce((sum: number, val: number) => sum + val, 0);
      const avgTips = totalWorkers > 0 ? Number((sumTips / totalWorkers).toFixed(2)) : 0;
      
      // Calculate rank sorting userAmounts descending
      const sortedAmounts = [...userAmounts].sort((a, b) => b - a);
      const myRank = sortedAmounts.indexOf(myAmount) + 1;

      // Variance vs store average
      const variancePercent = avgTips > 0 ? ((myAmount - avgTips) / avgTips) * 100 : 0;

      return {
        date: month,
        amount: myAmount,
        coworkerAverage: avgTips,
        totalWorkers,
        rank: myRank > 0 ? myRank : 1,
        variancePercent
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  const latestStats = chartData.length > 0 ? chartData[chartData.length - 1] : null;

  return (
    <div className="space-y-6">
      {/* Dynamic Coworker Benchmark Header Panel */}
      {latestStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-6 border border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-white">
            <div className="flex justify-between items-start">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Team Comparer</span>
              <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                <Users size={16} />
              </div>
            </div>
            <div className="mt-3">
              <span className="text-2xl font-black text-slate-800 tracking-tight">
                {formatCurrency(latestStats.coworkerAverage)}
              </span>
              <p className="text-[10px] font-medium text-slate-400 mt-1 uppercase tracking-wider">
                Store average per coworker
              </p>
            </div>
          </div>

          <div className="card p-6 border border-emerald-100 bg-gradient-to-br from-emerald-50/20 to-white">
            <div className="flex justify-between items-start">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Your Standing</span>
              <div className={`p-2 rounded-xl ${latestStats.variancePercent >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                {latestStats.variancePercent >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
              </div>
            </div>
            <div className="mt-3">
              <span className={`text-2xl font-black tracking-tight ${latestStats.variancePercent >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {latestStats.variancePercent >= 0 ? '+' : ''}{formatPercentage(latestStats.variancePercent, 1)}
              </span>
              <p className="text-[10px] font-medium text-slate-400 mt-1 uppercase tracking-wider">
                {latestStats.variancePercent >= 0 ? 'Above' : 'Below'} coworkers average
              </p>
            </div>
          </div>

          <div className="card p-6 border border-purple-100 bg-gradient-to-br from-purple-50/20 to-white">
            <div className="flex justify-between items-start">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Store Rank</span>
              <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
                <Award size={16} />
              </div>
            </div>
            <div className="mt-3">
              <span className="text-2xl font-black text-slate-800 tracking-tight">
                #{latestStats.rank} <span className="text-sm font-bold text-slate-400">of {latestStats.totalWorkers}</span>
              </span>
              <p className="text-[10px] font-medium text-slate-400 mt-1 uppercase tracking-wider">
                Active tip earners in your store
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recharts Area + Line Chart */}
      <div className="card p-8 bg-slate-900 text-white overflow-hidden relative border border-slate-850">
        <div className="absolute top-0 right-0 p-8 opacity-5">
          <TrendingUp size={120} />
        </div>
        <div className="relative z-10">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-black uppercase italic tracking-tighter">My Tips Benchmark</h3>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">
                Comparing your tip performance against coworker averages
              </p>
            </div>
          </div>
          
          <div className="h-56 mt-8">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="colorMyTips" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 9, fontWeight: 900 }}
                  dy={10}
                />
                <YAxis 
                  stroke="rgba(255,255,255,0.5)" 
                  fontSize={9} 
                  fontWeight={900} 
                  tickFormatter={(v) => `$${v}`}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e293b', color: '#fff' }}
                  itemStyle={{ fontSize: '10px', fontWeight: 900 }}
                  labelStyle={{ fontWeight: 900, marginBottom: '4px', textTransform: 'uppercase', color: '#94a3b8' }}
                  formatter={(value: any, name: any) => {
                    const formattedValue = formatCurrency(parseFloat(value));
                    const label = name === 'Amount' || name === 'amount' ? 'Amount' : 'Co-Worker Average';
                    return [formattedValue, label];
                  }}
                />
                <Legend 
                  verticalAlign="top" 
                  height={36} 
                  iconType="circle"
                  formatter={(value: string) => (
                    <span className="text-xs font-bold text-slate-300 mx-1">
                      {value}
                    </span>
                  )}
                />
                <Area 
                  type="monotone" 
                  name="Amount"
                  dataKey="amount" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorMyTips)" 
                />
                <Line 
                  type="monotone" 
                  name="Co-Worker Average"
                  dataKey="coworkerAverage" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 4, stroke: '#10b981', strokeWidth: 2, fill: '#0f172a' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Historical Detailed Comparison List */}
      <div className="card p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">Compare Earnings Backlog</h3>
            <p className="text-[10px] uppercase font-bold text-gray-400 mt-1">Detailed list of each tip period ending compared dynamically with coworkers</p>
          </div>
          <Calendar size={18} className="text-gray-400" />
        </div>
        <div className="space-y-4">
          {[...chartData].reverse().map(({ date, amount, coworkerAverage, rank, totalWorkers, variancePercent }) => (
            <div key={date} className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-gray-50 rounded-[1.5rem] border border-gray-100 group hover:bg-blue-50/55 transition-colors gap-4">
              <div className="space-y-1">
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Period Ending</span>
                <span className="font-black text-slate-800 italic uppercase text-sm block">{date}</span>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 rounded-md text-[9px] font-black bg-slate-200/50 text-slate-600 uppercase tracking-wider">
                    Store Rank #{rank} <span className="opacity-60">of {totalWorkers}</span>
                  </span>
                  <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider flex items-center gap-0.5 ${
                    variancePercent >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                  }`}>
                    {variancePercent >= 0 ? '+' : ''}{formatPercentage(variancePercent, 1)} vs Avg
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-6 sm:text-right">
                <div>
                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-0.5">Store Average</span>
                  <span className="text-sm font-black text-slate-600 block">{formatCurrency(coworkerAverage)}</span>
                </div>
                <div className="h-8 w-[1px] bg-slate-200"></div>
                <div>
                  <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest block mb-0.5">Your Earnings</span>
                  <span className="text-xl font-black text-emerald-600 block">{formatCurrency(amount)}</span>
                </div>
              </div>
            </div>
          ))}
          {chartData.length === 0 && <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] text-center py-10">No earning cycles detected...</p>}
        </div>
      </div>
    </div>
  );
};
